/**
 * SubscriptionBilling — new billing page for the Pro Plan subscription.
 *
 * Layout
 * ──────
 * Left column  : Plan card + Subscribe / Cancel form (Stripe Elements)
 * Right column : Subscription status + Quota meter + Payment history
 *
 * Stripe flow
 * ───────────
 * 1. Click "Subscribe"  → POST /api/v2/billing/setup-intent → {client_secret}
 * 2. stripe.confirmCardSetup(client_secret, {payment_method: {card}})
 * 3. On success         → POST /api/v2/billing/subscribe     → {subscription_id}
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, Spinner } from 'react-bootstrap';
import {
    CardElement,
    Elements,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe, StripeCardElementOptions } from '@stripe/stripe-js';
import {
    CheckCircleFill,
    ExclamationTriangleFill,
    XCircleFill,
    ArrowClockwise,
    CreditCard2FrontFill,
    LightningFill,
    XLg,
    PencilSquare,
    Trash3Fill,
} from 'react-bootstrap-icons';
import {
    cancelSubscription,
    changeSubscriptionPlan,
    confirmSubscriptionPayment,
    confirmLLMUsageCreditsRecharge,
    createSetupIntent,
    type BillingPaymentsFilterParams,
    type BillingUsageFilterParams,
    fetchPublicConfig,
    fetchPaymentHistory,
    fetchInitialUsageCreditCompletion,
    fetchInitialUsageCreditPreflight,
    fetchPaymentMethod,
    fetchPaymentRecovery,
    fetchQuotaStatus,
    fetchSubscriptionStatus,
    fetchTokenUsageHistory,
    rechargeLLMUsageCredits,
    retrySubscriptionPayment,
    removePaymentMethod,
    savePaymentMethod,
    syncSubscription,
    type BillingPaymentMethod,
    type PaymentRecovery,
    type QuotaStatus,
    type SubscriptionPayment,
    type SubscriptionStatus,
    type TokenUsageHistoryItem,
    type TokenLedgerItem,
} from '../api/stripe_billing';
import {
    fetchAvailablePlans,
    fetchCurrentPlan,
    type AvailablePlansResponse,
    type CurrentPlanResponse,
} from '../api/plans';
import CustomDropdown from '../components/CustomDropdown';
import { getGuidedTourStepIndexById } from '../tour/dashboardGuidedTourConfig';
import { BILLING_PAYMENT_FORM_EVENT } from '../tour/GuidedTourContext';
import {
    GUIDED_TOUR_STORAGE_KEYS,
    getTourStorageValue,
    markOnboardingBillingDecisionCompleted,
} from '../tour/guidedTourStorage';
import BillingPaymentProgressModal, {
    type BillingPaymentProgressStep,
} from '../components/BillingPaymentProgressModal';
import './SubscriptionBilling.css';
import { createBillingSupportTicket } from '../api/support_tickets';

// ---------------------------------------------------------------------------
// Stripe initialisation
// ---------------------------------------------------------------------------

const DEFAULT_STRIPE_LOAD_ERROR =
    'Stripe.js could not be loaded. Check access to js.stripe.com and m.stripe.network, then reload this page and try again.';
const INITIAL_CREDIT_COMPLETION_POLL_LIMIT = 45;
const INITIAL_CREDIT_COMPLETION_POLL_DELAY_MS = 1000;

function createStripePromise(
    publishableKey: string,
    onLoadError: (message: string) => void,
): Promise<Stripe | null>
{
    return loadStripe(publishableKey)
        .then((stripeClient) =>
        {
            if (!stripeClient)
            {
                onLoadError(DEFAULT_STRIPE_LOAD_ERROR);
            }
            return stripeClient;
        })
        .catch((err: any) =>
        {
            onLoadError(err?.message || DEFAULT_STRIPE_LOAD_ERROR);
            return null;
        });
}

const CARD_OPTIONS: StripeCardElementOptions = {
    hidePostalCode: true,
    style: {
        base: {
            fontSize: '15px',
            color: '#112043',
            fontFamily: '"Inter", system-ui, sans-serif',
            '::placeholder': { color: '#8da0bc' },
        },
        invalid: { color: '#b42318' },
    },
};

const SUPPORT_TICKET_REASON_OPTIONS = [
    { value: 'incorrect_amount', label: 'Incorrect amount' },
    { value: 'duplicate_charge', label: 'Duplicate charge' },
    { value: 'unrecognized_charge', label: 'Unrecognized charge' },
    { value: 'refund_request', label: 'Refund request' },
    { value: 'other', label: 'Other' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatUsd(cents: number): string
{
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(cents / 100);
}

function formatUsdAmount(amount: number): string
{
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

function formatLlmUsdAmount(amount: number | null | undefined): string
{
    const value = Number(amount ?? 0);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: Math.abs(value) > 0 && Math.abs(value) < 0.01 ? 8 : 2,
    }).format(value);
}

function formatTokens(n: number): string
{
    if (n >= 1_000_000_000)
    {
        return `${(n / 1_000_000_000).toFixed(1)}B`;
    }
    if (n >= 1_000_000)
    {
        return `${(n / 1_000_000).toFixed(1)}M`;
    }
    if (n >= 1_000)
    {
        return `${(n / 1_000).toFixed(0)}K`;
    }
    return n.toLocaleString();
}

function formatDate(iso: string | null | undefined): string
{
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

function formatBillingInterval(interval: string | null | undefined): string
{
    if (interval === 'day') return 'day';
    if (interval === 'month') return 'month';
    if (interval === 'year') return 'year';
    return interval || 'period';
}

function formatDateTime(iso: string | null | undefined): string
{
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d.getTime())
        ? '—'
        : d.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
}

function formatTimeUntil(iso: string | null | undefined): string | null
{
    if (!iso) return null;
    const remainingMs = new Date(iso).getTime() - Date.now();
    if (!Number.isFinite(remainingMs) || remainingMs <= 0) return null;

    const remainingMinutes = Math.ceil(remainingMs / 60_000);
    if (remainingMinutes < 60) return `${remainingMinutes} minute${remainingMinutes === 1 ? '' : 's'}`;

    const remainingHours = Math.floor(remainingMinutes / 60);
    const minutes = remainingMinutes % 60;
    return minutes
        ? `${remainingHours} hour${remainingHours === 1 ? '' : 's'} ${minutes} minute${minutes === 1 ? '' : 's'}`
        : `${remainingHours} hour${remainingHours === 1 ? '' : 's'}`;
}

function formatWorkflowProfile(value: string | null | undefined): string
{
    if (!value) return '—';
    return value
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function formatKeySource(value: string | null | undefined): string
{
    if (value === 'byo_key') return 'BYO key';
    if (value === 'platform') return 'Platform';
    return value || '—';
}

function formatUsageReference(entry: TokenUsageHistoryItem): string
{
    if (entry.job_id != null)
    {
        return `Job #${entry.job_id}`;
    }
    if (entry.chat_session_id)
    {
        return `Session ${entry.chat_session_id.length > 22
            ? `${entry.chat_session_id.slice(0, 12)}...${entry.chat_session_id.slice(-6)}`
            : entry.chat_session_id}`;
    }
    if (entry.usage_group_key)
    {
        return entry.usage_group_key.length > 28
            ? `${entry.usage_group_key.slice(0, 18)}...${entry.usage_group_key.slice(-6)}`
            : entry.usage_group_key;
    }
    return '—';
}

function formatUsageBillingStatus(entry: TokenUsageHistoryItem): string
{
    if (entry.billable_tokens > 0 && entry.non_billable_tokens > 0)
    {
        return 'Mixed';
    }
    return entry.billable ? 'Billable' : 'Non-billable';
}

function formatEventType(eventType: string): string
{
    const labels: Record<string, string> = {
        free_quota_granted: 'Free quota granted',
        job_usage: 'Job usage',
        free_overage_created: 'Free overage debt',
        free_quota_overage: 'Free quota overage',
    };
    return labels[eventType] || eventType.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

function groupLedgerEntries(entries: TokenLedgerItem[]): TokenLedgerItem[]
{
    const usageByKey = new Map<string, TokenLedgerItem>();
    const pairedUsageIds = new Set<number>();
    const groupedDebtById = new Map<number, TokenLedgerItem>();
    for (const entry of entries)
    {
        if (entry.event_type === 'job_usage' && entry.job_id != null)
        {
            usageByKey.set(`${entry.job_id}:${entry.created_at}:${Math.abs(entry.token_delta)}`, entry);
        }
    }

    for (const entry of entries)
    {
        if (entry.event_type === 'free_overage_created' && entry.job_id != null)
        {
            const usage = usageByKey.get(`${entry.job_id}:${entry.created_at}:${Math.abs(entry.token_delta)}`);
            if (usage)
            {
                pairedUsageIds.add(usage.id);
                groupedDebtById.set(entry.id, {
                    ...entry,
                    event_type: 'free_quota_overage',
                    token_delta: -Math.abs(usage.token_delta),
                    notes: `Job consumed ${Math.abs(usage.token_delta).toLocaleString()} tokens after free quota was exhausted; overage debt recorded.`,
                });
            }
        }
    }

    return entries.flatMap((entry) =>
    {
        if (pairedUsageIds.has(entry.id))
        {
            return [];
        }
        const groupedDebt = groupedDebtById.get(entry.id);
        if (groupedDebt)
        {
            return [groupedDebt];
        }
        return [entry];
    });
}

function formatPaymentType(payment: SubscriptionPayment): string
{
    if (payment.payment_type === 'llm_usage_recharge')
    {
        const rechargeLabels: Record<string, string> = {
            initial_recharge: 'Initial Credits',
            auto_recharge: 'Auto-Recharge',
            manual_recharge: 'Manual Recharge',
            overage_debt_settlement_recharge: 'Overage Debt Settlement',
        };
        return rechargeLabels[payment.recharge_type || ''] || 'LLM Usage Credits';
    }
    return 'Subscription';
}

function formatCardBrand(brand: string | null | undefined): string
{
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1);
}

function formatCardExpiry(month: number | null | undefined, year: number | null | undefined): string
{
    if (!month || !year) return '—';
    return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
}

function statusClass(s: string | null): string
{
    if (!s) return 'sb-status-inactive';
    switch (s.toLowerCase())
    {
        case 'active': return 'sb-status-active';
        case 'past_due': return 'sb-status-past_due';
        case 'cancelled': return 'sb-status-cancelled';
        default: return 'sb-status-inactive';
    }
}

function payStatusClass(s: string): string
{
    switch (s.toLowerCase())
    {
        case 'succeeded': return 'succeeded';
        case 'partially_refunded': return 'partially-refunded';
        case 'failed': return 'failed';
        default: return 'pending';
    }
}

function formatPaymentStatus(status: string): string
{
    const labels: Record<string, string> = {
        partially_refunded: 'Partially Refunded',
        refunded: 'Refunded',
    };
    const normalized = status.toLowerCase();
    return labels[normalized] || normalized.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

const PAYMENT_STATUS_OPTIONS = [
    { value: '', label: 'All Statuses' },
    { value: 'succeeded', label: 'Succeeded' },
    { value: 'pending', label: 'Pending' },
    { value: 'failed', label: 'Failed' },
    { value: 'partially_refunded', label: 'Partially Refunded' },
    { value: 'refunded', label: 'Refunded' },
];

const PAYMENT_TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'subscription', label: 'Subscription' },
    { value: 'initial_recharge', label: 'Initial Credits' },
    { value: 'auto_recharge', label: 'Auto-Recharge' },
    { value: 'manual_recharge', label: 'Manual Recharge' },
    { value: 'overage_debt_settlement_recharge', label: 'Overage Debt Settlement' },
];

const USAGE_KEY_OPTIONS = [
    { value: '', label: 'All Keys' },
    { value: 'platform', label: 'Platform' },
    { value: 'byo_key', label: 'BYO key' },
];

const USAGE_BILLABLE_OPTIONS = [
    { value: '', label: 'All Billing' },
    { value: 'true', label: 'Billable' },
    { value: 'false', label: 'Non-billable' },
];

const LEDGER_BUCKET_OPTIONS = [
    { value: '', label: 'All Buckets' },
    { value: 'free', label: 'Free' },
    { value: 'paid', label: 'Paid' },
    { value: 'system', label: 'System' },
];

// ---------------------------------------------------------------------------
// Outer wrapper (provides Stripe Elements context)
// ---------------------------------------------------------------------------

export default function SubscriptionBillingPage(): React.ReactElement
{
    const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);
    const [stripeConfigured, setStripeConfigured] = useState(false);
    const [stripeConfigResolved, setStripeConfigResolved] = useState(false);
    const [stripeConfigError, setStripeConfigError] = useState<string | null>(null);
    const [stripeLoadError, setStripeLoadError] = useState<string | null>(null);

    useEffect(() =>
    {
        let cancelled = false;

        function handleStripeLoadError(message: string): void
        {
            if (cancelled)
            {
                return;
            }
            setStripeLoadError(message);
        }

        async function initializeStripe(): Promise<void>
        {
            setStripeLoadError(null);

            try
            {
                const config = await fetchPublicConfig();
                const publishableKey = config.publishable_key.trim();
                if (!publishableKey)
                {
                    throw new Error('Stripe publishable key is missing from backend billing configuration.');
                }

                if (cancelled)
                {
                    return;
                }

                setStripePromise(createStripePromise(publishableKey, handleStripeLoadError));
                setStripeConfigured(true);
                setStripeConfigError(null);
                setStripeConfigResolved(true);
                return;
            }
            catch (err: any)
            {
                if (cancelled)
                {
                    return;
                }

                const msg = err?.response?.data?.detail || err?.message || 'Stripe is not fully configured in this environment.';
                setStripePromise(null);
                setStripeConfigured(false);
                setStripeConfigError(msg);
                setStripeConfigResolved(true);
            }
        }

        void initializeStripe();

        return () =>
        {
            cancelled = true;
        };
    }, []);

    if (!stripeConfigResolved)
    {
        return (
            <div className="sb-page">
                <div className="sb-loading">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading Stripe billing form…
                </div>
            </div>
        );
    }

    return (
        <Elements stripe={stripePromise}>
            <SubscriptionBillingContent
                stripeConfigured={stripeConfigured}
                stripeConfigError={stripeConfigError}
                stripeLoadError={stripeLoadError}
            />
        </Elements>
    );
}

// ---------------------------------------------------------------------------
// Main page content
// ---------------------------------------------------------------------------

interface SubscriptionBillingContentProps
{
    stripeConfigured: boolean;
    stripeConfigError: string | null;
    stripeLoadError: string | null;
}

type UsageSection = 'usage' | 'ledger';

function SubscriptionBillingContent({
    stripeConfigured,
    stripeConfigError,
    stripeLoadError,
}: SubscriptionBillingContentProps): React.ReactElement
{
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const stripe = useStripe();
    const elements = useElements();

    // Page-level state
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState<string | null>(null);
    const [successMsg, setSuccessMsg] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Data
    const [subStatus, setSubStatus] = useState<SubscriptionStatus | null>(null);
    const [quota, setQuota] = useState<QuotaStatus | null>(null);
    const [payments, setPayments] = useState<SubscriptionPayment[]>([]);
    const [tokenUsageHistory, setTokenUsageHistory] = useState<TokenUsageHistoryItem[]>([]);
    const [ledgerEntries, setLedgerEntries] = useState<TokenLedgerItem[]>([]);
    const displayLedgerEntries = useMemo(() => groupLedgerEntries(ledgerEntries), [ledgerEntries]);
    const [paymentMethod, setPaymentMethod] = useState<BillingPaymentMethod | null>(null);
    const [paymentRecovery, setPaymentRecovery] = useState<PaymentRecovery | null>(null);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageLoadingSection, setUsageLoadingSection] = useState<UsageSection | null>(null);
    const [paymentsError, setPaymentsError] = useState<string | null>(null);
    const [usageError, setUsageError] = useState<string | null>(null);

    const [cardComplete, setCardComplete] = useState(false);

    // Plan picker state
    const [availablePlans, setAvailablePlans] = useState<AvailablePlansResponse | null>(null);
    const [currentPlanInfo, setCurrentPlanInfo] = useState<CurrentPlanResponse | null>(null);

    // Payment method state
    const [showPaymentMethodForm, setShowPaymentMethodForm] = useState(false);
    const [savingPaymentMethod, setSavingPaymentMethod] = useState(false);
    const [showRemovePaymentConfirm, setShowRemovePaymentConfirm] = useState(false);
    const [removingPaymentMethod, setRemovingPaymentMethod] = useState(false);

    // Cancel confirm state
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [resumingSubscription, setResumingSubscription] = useState(false);
    const [rechargingCredits, setRechargingCredits] = useState(false);
    const [showRechargeCreditsConfirm, setShowRechargeCreditsConfirm] = useState(false);
    const [supportPayment, setSupportPayment] = useState<SubscriptionPayment | null>(null);
    const [supportReason, setSupportReason] = useState('incorrect_amount');
    const [supportDescription, setSupportDescription] = useState('');
    const [supportSubmitting, setSupportSubmitting] = useState(false);
    const [recoveringSubscriptionPayment, setRecoveringSubscriptionPayment] = useState(false);
    const [paymentProgress, setPaymentProgress] = useState<BillingPaymentProgressStep[] | null>(null);

    const [paymentSearchInput, setPaymentSearchInput] = useState('');
    const [paymentStatusInput, setPaymentStatusInput] = useState('');
    const [paymentTypeInput, setPaymentTypeInput] = useState('');
    const [paymentFromDateInput, setPaymentFromDateInput] = useState('');
    const [paymentToDateInput, setPaymentToDateInput] = useState('');
    const [appliedPaymentFilters, setAppliedPaymentFilters] = useState<BillingPaymentsFilterParams>({});

    const [usageSearchInput, setUsageSearchInput] = useState('');
    const [usageKeySourceInput, setUsageKeySourceInput] = useState('');
    const [usageBillableInput, setUsageBillableInput] = useState('');
    const [usageFromDateInput, setUsageFromDateInput] = useState('');
    const [usageToDateInput, setUsageToDateInput] = useState('');
    const [ledgerSearchInput, setLedgerSearchInput] = useState('');
    const [ledgerBucketInput, setLedgerBucketInput] = useState('');
    const [ledgerFromDateInput, setLedgerFromDateInput] = useState('');
    const [ledgerToDateInput, setLedgerToDateInput] = useState('');
    const [appliedUsageFilters, setAppliedUsageFilters] = useState<BillingUsageFilterParams>({});
    const initialBillingLoadStarted = useRef(false);

    const updatePaymentProgress = (id: string, state: BillingPaymentProgressStep['state'], detail: string): void =>
    {
        setPaymentProgress((current) => current?.map((step) => (
            step.id === id ? { ...step, state, detail } : step
        )) || null);
    };

    const paymentInProgress = paymentProgress?.some((step) => !['succeeded', 'failed'].includes(step.state)) === true;

    useEffect(() =>
    {
        if (!paymentInProgress) return undefined;

        const preventExit = (event: BeforeUnloadEvent): void =>
        {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', preventExit);
        return () => window.removeEventListener('beforeunload', preventExit);
    }, [paymentInProgress]);

    const onboardingRequested = searchParams.get('guidedTour') === '1';
    const onboardingOrganizationCreated = getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.organizationCreated) === 'true';
    const onboardingBillingCompleted = getTourStorageValue(GUIDED_TOUR_STORAGE_KEYS.billingDecisionCompleted) === 'true';
    const onboardingBillingRequested = onboardingRequested || (onboardingOrganizationCreated && !onboardingBillingCompleted);

    useEffect(() =>
    {
        const billingError = (location.state as { billingError?: unknown } | null)?.billingError;
        if (typeof billingError === 'string' && billingError)
        {
            setErrorMsg(billingError);
        }
    }, [location.state]);

    useEffect(() =>
    {
        function handleShowPaymentForm(): void
        {
            setShowRemovePaymentConfirm(false);
            setShowPaymentMethodForm(true);
            setErrorMsg(null);
        }

        window.addEventListener(BILLING_PAYMENT_FORM_EVENT, handleShowPaymentForm);
        return () => window.removeEventListener(BILLING_PAYMENT_FORM_EVENT, handleShowPaymentForm);
    }, []);

    // ---------------------------------------------------------------------------
    // Data loading
    // ---------------------------------------------------------------------------

    const loadPayments = useCallback(async (filters: BillingPaymentsFilterParams) =>
    {
        setPaymentsLoading(true);
        setPaymentsError(null);
        try
        {
            const response = await fetchPaymentHistory({ limit: 100, ...filters });
            setPayments(response.items);
        }
        catch (err: any)
        {
            setPaymentsError(err?.response?.data?.detail || err?.message || 'Failed to load payment history');
            setPayments([]);
        }
        finally
        {
            setPaymentsLoading(false);
        }
    }, []);

    const loadUsageHistory = useCallback(async (filters: BillingUsageFilterParams, section?: UsageSection) =>
    {
        setUsageLoading(true);
        setUsageLoadingSection(section || null);
        setUsageError(null);
        try
        {
            const response = await fetchTokenUsageHistory({ limit: 100, ...filters });
            setTokenUsageHistory(response.items);
            setLedgerEntries(response.ledger_entries || []);
        }
        catch (err: any)
        {
            setUsageError(err?.response?.data?.detail || err?.message || 'Failed to load token usage history');
            setTokenUsageHistory([]);
            setLedgerEntries([]);
        }
        finally
        {
            setUsageLoading(false);
            setUsageLoadingSection(null);
        }
    }, []);

    const loadData = useCallback(async () =>
    {
        setLoading(true);
        setPageError(null);
        try
        {
            const results = await Promise.allSettled([
                fetchSubscriptionStatus(),
                fetchQuotaStatus(),
                fetchTokenUsageHistory({ limit: 100, ...appliedUsageFilters }),
                fetchPaymentMethod(),
                fetchAvailablePlans(),
                fetchCurrentPlan(),
                fetchPaymentRecovery().catch((error) => {
                    if (error?.response?.status === 403) return null;
                    throw error;
                }),
            ]);

            const [statusRes, quotaRes, tokenUsageRes, paymentMethodRes, plansRes, currentPlanRes, recoveryRes] = results;
            const failures = results.filter((result) => result.status === 'rejected');
            const hasPaidPlans = plansRes.status === 'fulfilled' && plansRes.value.paid_plans.length > 0;
            const firstFailure = failures[0];
            const firstFailureMessage = firstFailure && firstFailure.status === 'rejected'
                ? firstFailure.reason?.response?.data?.detail || firstFailure.reason?.message || 'Failed to load billing data'
                : null;

            if (statusRes.status === 'fulfilled') {
                setSubStatus(statusRes.value);
            } else {
                setSubStatus(null);
            }

            if (quotaRes.status === 'fulfilled') {
                setQuota(quotaRes.value);
            } else {
                setQuota(null);
            }

            if (tokenUsageRes.status === 'fulfilled') {
                setTokenUsageHistory(tokenUsageRes.value.items);
                setLedgerEntries(tokenUsageRes.value.ledger_entries || []);
                setUsageError(null);
            } else {
                setTokenUsageHistory([]);
                setLedgerEntries([]);
                setUsageError(tokenUsageRes.reason?.response?.data?.detail || tokenUsageRes.reason?.message || 'Failed to load token usage history');
            }

            if (paymentMethodRes.status === 'fulfilled') {
                setPaymentMethod(paymentMethodRes.value);
                if (!paymentMethodRes.value.has_payment_method && hasPaidPlans) {
                    setShowPaymentMethodForm(true);
                    setShowRemovePaymentConfirm(false);
                } else if (!hasPaidPlans) {
                    setShowPaymentMethodForm(false);
                    setShowRemovePaymentConfirm(false);
                }
            } else {
                setPaymentMethod(null);
                if (!hasPaidPlans) {
                    setShowPaymentMethodForm(false);
                    setShowRemovePaymentConfirm(false);
                }
            }

            if (plansRes.status === 'fulfilled') {
                setAvailablePlans(plansRes.value);
            } else {
                setAvailablePlans(null);
            }

            if (currentPlanRes.status === 'fulfilled') {
                setCurrentPlanInfo(currentPlanRes.value);
            } else {
                setCurrentPlanInfo(null);
            }

            if (recoveryRes.status === 'fulfilled') {
                setPaymentRecovery(recoveryRes.value);
            } else {
                setPaymentRecovery(null);
            }

            if (failures.length > 0 && firstFailureMessage) {
                setPageError(
                    failures.length === results.length
                        ? firstFailureMessage
                        : `Some billing information could not be loaded. ${firstFailureMessage}`
                );
            }
        }
        catch (err: any)
        {
            const msg = err?.response?.data?.detail || err?.message || 'Failed to load billing data';
            setPageError(msg);
        }
        finally
        {
            setLoading(false);
        }
    }, [appliedUsageFilters]);

    const syncAndLoadData = useCallback(async () =>
    {
        setLoading(true);
        let syncError: string | null = null;
        try
        {
            await syncSubscription();
        }
        catch (err: any)
        {
            if (err?.response?.status !== 404)
            {
                syncError = err?.response?.data?.detail || err?.message || 'Could not synchronize subscription status with Stripe.';
            }
        }

        await loadData();
        if (syncError)
        {
            setPageError((current) => current ? `${current} ${syncError}` : syncError);
        }
    }, [loadData]);

    const handleBillingRefresh = useCallback(() =>
    {
        setSuccessMsg(null);
        setErrorMsg(null);
        setPageError(null);
        navigate(`${location.pathname}${location.search}${location.hash}`, {
            replace: true,
            state: null,
        });
        void syncAndLoadData();
    }, [location.hash, location.pathname, location.search, navigate, syncAndLoadData]);

    useEffect(() =>
    {
        if (initialBillingLoadStarted.current)
        {
            return;
        }
        initialBillingLoadStarted.current = true;
        void syncAndLoadData();
    }, [syncAndLoadData]);

    function applyPaymentFilters(): void
    {
        const filters: BillingPaymentsFilterParams = {};
        if (paymentSearchInput.trim()) filters.search = paymentSearchInput.trim();
        if (paymentStatusInput) filters.status = paymentStatusInput;
        if (paymentTypeInput) filters.payment_type = paymentTypeInput;
        if (paymentFromDateInput) filters.date_from = paymentFromDateInput;
        if (paymentToDateInput) filters.date_to = paymentToDateInput;
        setAppliedPaymentFilters(filters);
        void loadPayments(filters);
    }

    function clearPaymentFilters(): void
    {
        setPaymentSearchInput('');
        setPaymentStatusInput('');
        setPaymentTypeInput('');
        setPaymentFromDateInput('');
        setPaymentToDateInput('');
        setAppliedPaymentFilters({});
        void loadPayments({});
    }

    function applyUsageFilters(): void
    {
        const filters: BillingUsageFilterParams = { ...appliedUsageFilters };
        if (usageSearchInput.trim()) filters.usage_search = usageSearchInput.trim();
        else delete filters.usage_search;
        if (usageKeySourceInput) filters.usage_key_source = usageKeySourceInput;
        else delete filters.usage_key_source;
        if (usageBillableInput === 'true') filters.usage_billable = true;
        else if (usageBillableInput === 'false') filters.usage_billable = false;
        else delete filters.usage_billable;
        if (usageFromDateInput) filters.usage_date_from = usageFromDateInput;
        else delete filters.usage_date_from;
        if (usageToDateInput) filters.usage_date_to = usageToDateInput;
        else delete filters.usage_date_to;
        setAppliedUsageFilters(filters);
        void loadUsageHistory(filters, 'usage');
    }

    function clearUsageFilters(): void
    {
        setUsageSearchInput('');
        setUsageKeySourceInput('');
        setUsageBillableInput('');
        setUsageFromDateInput('');
        setUsageToDateInput('');
        const {
            usage_search: _usageSearch,
            usage_key_source: _usageKeySource,
            usage_billable: _usageBillable,
            usage_date_from: _usageDateFrom,
            usage_date_to: _usageDateTo,
            ...rest
        } = appliedUsageFilters;
        const filters: BillingUsageFilterParams = { ...rest };
        setAppliedUsageFilters(filters);
        void loadUsageHistory(filters, 'usage');
    }

    function applyLedgerFilters(): void
    {
        const filters: BillingUsageFilterParams = { ...appliedUsageFilters };
        if (ledgerSearchInput.trim()) filters.ledger_search = ledgerSearchInput.trim();
        else delete filters.ledger_search;
        if (ledgerBucketInput) filters.ledger_balance_bucket = ledgerBucketInput;
        else delete filters.ledger_balance_bucket;
        if (ledgerFromDateInput) filters.ledger_date_from = ledgerFromDateInput;
        else delete filters.ledger_date_from;
        if (ledgerToDateInput) filters.ledger_date_to = ledgerToDateInput;
        else delete filters.ledger_date_to;
        setAppliedUsageFilters(filters);
        void loadUsageHistory(filters, 'ledger');
    }

    function clearLedgerFilters(): void
    {
        setLedgerSearchInput('');
        setLedgerBucketInput('');
        setLedgerFromDateInput('');
        setLedgerToDateInput('');
        const {
            ledger_search: _ledgerSearch,
            ledger_balance_bucket: _ledgerBucket,
            ledger_date_from: _ledgerDateFrom,
            ledger_date_to: _ledgerDateTo,
            ...rest
        } = appliedUsageFilters;
        const filters: BillingUsageFilterParams = { ...rest };
        setAppliedUsageFilters(filters);
        void loadUsageHistory(filters, 'ledger');
    }

    function renderUsageFilters(): React.ReactNode
    {
        const hasActiveFilters = !!(
            appliedUsageFilters.usage_search
            || appliedUsageFilters.usage_key_source
            || appliedUsageFilters.usage_billable !== undefined
            || appliedUsageFilters.usage_date_from
            || appliedUsageFilters.usage_date_to
        );

        return (
            <div className="sb-filters">
                <div className="sb-filter-field sb-filter-search">
                    <input className="sb-filter-input" type="text" placeholder="Search usage, job, session, model..." value={usageSearchInput} onChange={(e) => setUsageSearchInput(e.target.value)} />
                </div>
                <div className="sb-filter-field sb-filter-dropdown">
                    <CustomDropdown value={usageKeySourceInput} options={USAGE_KEY_OPTIONS} onChange={(v) => setUsageKeySourceInput(v)} placeholder="All Keys" />
                </div>
                <div className="sb-filter-field sb-filter-dropdown">
                    <CustomDropdown value={usageBillableInput} options={USAGE_BILLABLE_OPTIONS} onChange={(v) => setUsageBillableInput(v)} placeholder="All Billing" />
                </div>
                <div className="sb-filter-field sb-filter-date filter-date-ph-wrap">
                    <input className={`sb-filter-date-input${!usageFromDateInput ? ' date-empty' : ''}`} type="date" value={usageFromDateInput} onChange={(e) => setUsageFromDateInput(e.target.value)} />
                    {!usageFromDateInput && <span className="filter-date-ph" aria-hidden="true">Usage Start</span>}
                </div>
                <div className="sb-filter-field sb-filter-date filter-date-ph-wrap">
                    <input className={`sb-filter-date-input${!usageToDateInput ? ' date-empty' : ''}`} type="date" value={usageToDateInput} onChange={(e) => setUsageToDateInput(e.target.value)} />
                    {!usageToDateInput && <span className="filter-date-ph" aria-hidden="true">Usage End</span>}
                </div>
                <button className="sb-btn-search" type="button" onClick={() => applyUsageFilters()} disabled={usageLoading && usageLoadingSection === 'usage'}>Search</button>
                {hasActiveFilters && <button className="sb-btn-clear" type="button" onClick={() => clearUsageFilters()}>Clear</button>}
            </div>
        );
    }

    function renderLedgerFilters(): React.ReactNode
    {
        const hasActiveFilters = !!(
            appliedUsageFilters.ledger_search
            || appliedUsageFilters.ledger_balance_bucket
            || appliedUsageFilters.ledger_date_from
            || appliedUsageFilters.ledger_date_to
        );

        return (
            <div className="sb-filters">
                <div className="sb-filter-field sb-filter-search">
                    <input className="sb-filter-input" type="text" placeholder="Search ledger events and notes..." value={ledgerSearchInput} onChange={(e) => setLedgerSearchInput(e.target.value)} />
                </div>
                <div className="sb-filter-field sb-filter-dropdown">
                    <CustomDropdown value={ledgerBucketInput} options={LEDGER_BUCKET_OPTIONS} onChange={(v) => setLedgerBucketInput(v)} placeholder="All Buckets" />
                </div>
                <div className="sb-filter-field sb-filter-date filter-date-ph-wrap">
                    <input className={`sb-filter-date-input${!ledgerFromDateInput ? ' date-empty' : ''}`} type="date" value={ledgerFromDateInput} onChange={(e) => setLedgerFromDateInput(e.target.value)} />
                    {!ledgerFromDateInput && <span className="filter-date-ph" aria-hidden="true">Ledger Start</span>}
                </div>
                <div className="sb-filter-field sb-filter-date filter-date-ph-wrap">
                    <input className={`sb-filter-date-input${!ledgerToDateInput ? ' date-empty' : ''}`} type="date" value={ledgerToDateInput} onChange={(e) => setLedgerToDateInput(e.target.value)} />
                    {!ledgerToDateInput && <span className="filter-date-ph" aria-hidden="true">Ledger End</span>}
                </div>
                <button className="sb-btn-search" type="button" onClick={() => applyLedgerFilters()} disabled={usageLoading && usageLoadingSection === 'ledger'}>Search</button>
                {hasActiveFilters && <button className="sb-btn-clear" type="button" onClick={() => clearLedgerFilters()}>Clear</button>}
            </div>
        );
    }

    function renderPaymentFilters(): React.ReactNode
    {
        const hasActiveFilters = !!(
            appliedPaymentFilters.search
            || appliedPaymentFilters.status
            || appliedPaymentFilters.payment_type
            || appliedPaymentFilters.date_from
            || appliedPaymentFilters.date_to
        );

        return (
            <div className="sb-filters">
                <div className="sb-filter-field sb-filter-search">
                    <input className="sb-filter-input" type="text" placeholder="Search amount, Stripe IDs, status..." value={paymentSearchInput} onChange={(e) => setPaymentSearchInput(e.target.value)} />
                </div>
                <div className="sb-filter-field sb-filter-dropdown">
                    <CustomDropdown value={paymentStatusInput} options={PAYMENT_STATUS_OPTIONS} onChange={(v) => setPaymentStatusInput(v)} placeholder="All Statuses" />
                </div>
                <div className="sb-filter-field sb-filter-dropdown">
                    <CustomDropdown value={paymentTypeInput} options={PAYMENT_TYPE_OPTIONS} onChange={(v) => setPaymentTypeInput(v)} placeholder="All Types" />
                </div>
                <div className="sb-filter-field sb-filter-date filter-date-ph-wrap">
                    <input className={`sb-filter-date-input${!paymentFromDateInput ? ' date-empty' : ''}`} type="date" value={paymentFromDateInput} onChange={(e) => setPaymentFromDateInput(e.target.value)} />
                    {!paymentFromDateInput && <span className="filter-date-ph" aria-hidden="true">Start Date</span>}
                </div>
                <div className="sb-filter-field sb-filter-date filter-date-ph-wrap">
                    <input className={`sb-filter-date-input${!paymentToDateInput ? ' date-empty' : ''}`} type="date" value={paymentToDateInput} onChange={(e) => setPaymentToDateInput(e.target.value)} />
                    {!paymentToDateInput && <span className="filter-date-ph" aria-hidden="true">End Date</span>}
                </div>
                <button className="sb-btn-search" type="button" onClick={() => applyPaymentFilters()} disabled={paymentsLoading}>Search</button>
                {hasActiveFilters && <button className="sb-btn-clear" type="button" onClick={() => clearPaymentFilters()}>Clear</button>}
            </div>
        );
    }

    // ---------------------------------------------------------------------------
    // Payment method helpers / handlers
    // ---------------------------------------------------------------------------

    function closeCardForms(): void
    {
        setShowPaymentMethodForm(false);
        setShowRemovePaymentConfirm(false);
        setCardComplete(false);
        elements?.getElement(CardElement)?.clear();
    }

    async function confirmCardSetupAndGetPaymentMethodId(): Promise<string>
    {
        if (!stripe || !elements)
        {
            throw new Error('Stripe is still loading. Please try again in a moment.');
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement)
        {
            throw new Error('Card form not available. Please refresh and try again.');
        }

        const { client_secret } = await createSetupIntent();
        const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(client_secret, {
            payment_method: { card: cardElement },
        });

        if (stripeError)
        {
            throw new Error(stripeError.message || 'Card verification failed. Please check your card details.');
        }

        const paymentMethodId = typeof setupIntent?.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent?.payment_method?.id;

        if (!paymentMethodId)
        {
            throw new Error('Unable to retrieve payment method. Please try again.');
        }

        return paymentMethodId;
    }

    async function handleSavePaymentMethod(e: React.FormEvent): Promise<void>
    {
        e.preventDefault();
        setSavingPaymentMethod(true);
        setErrorMsg(null);
        setSuccessMsg(null);

        try
        {
            const paymentMethodId = await confirmCardSetupAndGetPaymentMethodId();
            const result = await savePaymentMethod({ payment_method_id: paymentMethodId });
            setSuccessMsg(result.message || 'Payment method saved successfully.');
            closeCardForms();
            await loadData();
        }
        catch (err: any)
        {
            const msg = err?.response?.data?.detail || err?.message || 'Could not save the payment method.';
            setErrorMsg(msg);
        }
        finally
        {
            setSavingPaymentMethod(false);
        }
    }

    async function handleRemovePaymentMethod(): Promise<void>
    {
        setRemovingPaymentMethod(true);
        setErrorMsg(null);
        setSuccessMsg(null);

        try
        {
            const result = await removePaymentMethod();
            setSuccessMsg(result.message || 'Payment method removed successfully.');
            setShowRemovePaymentConfirm(false);
            setShowPaymentMethodForm(true);
            setCardComplete(false);
            await loadData();
        }
        catch (err: any)
        {
            const msg = err?.response?.data?.detail || err?.message || 'Could not remove the payment method.';
            setErrorMsg(msg);
        }
        finally
        {
            setRemovingPaymentMethod(false);
        }
    }

    function completeOnboardingBillingDecision(): void
    {
        markOnboardingBillingDecisionCompleted(getGuidedTourStepIndexById('project-name'));
        navigate('/projects/new?guidedTour=1');
    }

    function handleContinueWithFreeQuota(): void
    {
        completeOnboardingBillingDecision();
    }

    function handleFocusSubscriptionPath(): void
    {
        const paymentCard = document.getElementById('billing-payment-method-card');
        paymentCard?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    function handleAddPaymentMethodForRecovery(): void
    {
        setShowRemovePaymentConfirm(false);
        setShowPaymentMethodForm(true);
        setCardComplete(false);
        setErrorMsg(null);
        window.requestAnimationFrame(handleFocusSubscriptionPath);
    }

    // ---------------------------------------------------------------------------
    // Cancel handler
    // ---------------------------------------------------------------------------

    async function handleCancel(): Promise<void>
    {
        setCancelling(true);
        setErrorMsg(null);
        setSuccessMsg(null);
        try
        {
            const result = await cancelSubscription();
            setSuccessMsg(result.message);
            setShowCancelConfirm(false);
            await loadData();
        }
        catch (err: any)
        {
            const msg = err?.response?.data?.detail || err?.message || 'Cancellation failed. Please try again.';
            setErrorMsg(msg);
        }
        finally
        {
            setCancelling(false);
        }
    }

    async function handleResumeSubscription(): Promise<void>
    {
        const planId = currentPlanInfo?.plan?.id ?? subStatus?.plan?.id;
        if (!planId)
        {
            setErrorMsg('The current subscription plan could not be found. Refresh and try again.');
            return;
        }

        setResumingSubscription(true);
        setErrorMsg(null);
        setSuccessMsg(null);
        try
        {
            const result = await changeSubscriptionPlan({
                plan_id: planId,
                idempotency_key: crypto.randomUUID(),
            });
            setSuccessMsg(result.message || 'Subscription resumed. Auto-renewal has been restored.');
            await loadData();
        }
        catch (err: any)
        {
            setErrorMsg(err?.response?.data?.detail || err?.message || 'Could not resume the subscription.');
        }
        finally
        {
            setResumingSubscription(false);
        }
    }

    async function handleManualCreditsRecharge(): Promise<void>
    {
        setShowRechargeCreditsConfirm(false);
        setRechargingCredits(true);
        setErrorMsg(null);
        setSuccessMsg(null);
        const storageKey = 'acceliqa:billing:manual-recharge-request';
        const requestKey = sessionStorage.getItem(storageKey) || crypto.randomUUID();
        sessionStorage.setItem(storageKey, requestKey);
        setPaymentProgress([{
            id: 'credits',
            label: 'LLM Usage Credits payment',
            state: 'processing',
            detail: 'Contacting Stripe to recharge your credits.',
        }]);
        try
        {
            let result = await rechargeLLMUsageCredits(requestKey);
            while (result.requires_action && result.client_secret && result.stripe_payment_intent_id)
            {
                if (!stripe)
                {
                    throw new Error('Stripe is still loading. Please try again in a moment.');
                }
                updatePaymentProgress('credits', 'verifying', 'Complete the card verification window to continue.');
                const confirmation = await stripe.confirmCardPayment(result.client_secret);
                if (confirmation.error)
                {
                    throw new Error(confirmation.error.message || 'Card verification was not completed.');
                }
                result = await confirmLLMUsageCreditsRecharge(result.stripe_payment_intent_id);
                updatePaymentProgress(
                    'credits',
                    result.requires_action
                        ? 'verifying'
                        : 'processing',
                    result.requires_action
                        ? 'Another LLM Usage Credits recharge needs card verification to settle the remaining debt.'
                        : 'Stripe is confirming the LLM Usage Credits payment.',
                );
            }
            sessionStorage.removeItem(storageKey);
            updatePaymentProgress('credits', 'succeeded', 'LLM Usage Credits were recharged successfully.');
            setSuccessMsg('LLM Usage Credits recharged successfully.');
            await loadData();
        }
        catch (err: any)
        {
            if (Number(err?.response?.status) === 402)
            {
                sessionStorage.removeItem(storageKey);
            }
            const msg = err?.response?.data?.detail || err?.message || 'LLM Usage Credits recharge failed.';
            setErrorMsg(msg);
            updatePaymentProgress('credits', 'failed', msg);
        }
        finally
        {
            setRechargingCredits(false);
        }
    }

    async function handleRecoverSubscriptionPayment(): Promise<void>
    {
        if (!paymentRecovery) return;
        const currentCreditSummary = quota?.llm_usage_credits || subStatus?.llm_usage_credits;
        const frontendInitialCreditRechargeNotExpected = Boolean(
            currentCreditSummary
            && currentCreditSummary.available_balance_after_debt_cents
                > currentCreditSummary.auto_recharge_threshold_cents,
        );
        setRecoveringSubscriptionPayment(true);
        setErrorMsg(null);
        setSuccessMsg(null);
        let initialCreditRechargeNotExpected = frontendInitialCreditRechargeNotExpected;
        try
        {
            const preflight = await fetchInitialUsageCreditPreflight();
            initialCreditRechargeNotExpected = preflight.state === 'skipped';
        }
        catch
        {
            // Use the latest displayed backend balance only when the read-only
            // preflight request is temporarily unavailable.
        }
        setPaymentProgress([{
            id: 'subscription',
            label: 'Subscription payment',
            state: 'processing',
            detail: 'Preparing the latest subscription invoice for payment.',
        }, {
            id: 'credits',
            label: 'Initial LLM Usage Credits',
            state: initialCreditRechargeNotExpected ? 'succeeded' : 'pending',
            detail: initialCreditRechargeNotExpected
                ? 'No initial recharge is expected. Your current available balance is above the auto-recharge threshold. We will confirm it after the subscription payment is completed.'
                : 'This charge starts after the subscription payment succeeds.',
        }]);
        try
        {
            let recovery: PaymentRecovery | null = paymentRecovery;
            if (!recovery.requires_action || !recovery.client_secret)
            {
                recovery = await retrySubscriptionPayment(recovery.stripe_invoice_id);
            }
            if (recovery?.requires_action)
            {
                if (!stripe || !recovery.client_secret || !recovery.stripe_payment_intent_id)
                {
                    throw new Error('Stripe payment verification is not ready. Refresh and try again.');
                }
                if (recovery.status === 'requires_payment_method' && !paymentMethod?.payment_method_id)
                {
                    throw new Error('Save a payment method before retrying this invoice.');
                }
                updatePaymentProgress('subscription', 'verifying', 'Complete the card verification window to continue.');
                const confirmation = recovery.status === 'requires_payment_method'
                    ? await stripe.confirmCardPayment(recovery.client_secret, {
                        payment_method: paymentMethod!.payment_method_id!,
                    })
                    : await stripe.confirmCardPayment(recovery.client_secret);
                if (confirmation.error)
                {
                    throw new Error(confirmation.error.message || 'Payment verification was not completed.');
                }
                const confirmedStatus = confirmation.paymentIntent?.status;
                if (confirmedStatus && !['succeeded', 'processing'].includes(confirmedStatus))
                {
                    throw new Error('Stripe has not completed the invoice payment yet.');
                }
                const result = await confirmSubscriptionPayment(recovery.stripe_payment_intent_id);
                updatePaymentProgress('subscription', 'processing', 'Stripe is confirming the subscription payment.');
                setSuccessMsg(result.message || 'Payment verified. Your subscription is being finalized.');
            }
            else if (recovery)
            {
                throw new Error(recovery.failure_message || 'Stripe has not completed the invoice payment yet.');
            }
            let synced = await syncSubscription();
            for (let attempt = 0; attempt < 6 && !['active', 'trialing'].includes(synced.status); attempt += 1)
            {
                await new Promise((resolve) => window.setTimeout(resolve, 750));
                synced = await syncSubscription();
            }
            if (!['active', 'trialing'].includes(synced.status))
            {
                throw new Error('Your subscription payment is still being finalized. Please wait a moment and try again.');
            }

            setPaymentRecovery(null);
            updatePaymentProgress('subscription', 'succeeded', 'Subscription payment completed.');
            if (!initialCreditRechargeNotExpected)
            {
                updatePaymentProgress('credits', 'processing', 'Preparing the initial LLM Usage Credits charge.');
            }
            setSuccessMsg(
                initialCreditRechargeNotExpected
                    ? 'Subscription payment confirmed. Confirming your current LLM Usage Credits balance...'
                    : 'Subscription payment confirmed. Preparing initial LLM Usage Credits...',
            );

            let initialCreditCompleted = false;
            for (let attempt = 0; attempt < INITIAL_CREDIT_COMPLETION_POLL_LIMIT; attempt += 1)
            {
                const creditCompletion = await fetchInitialUsageCreditCompletion(synced.subscription_id);
                if (creditCompletion.state === 'pending' && initialCreditRechargeNotExpected)
                {
                    updatePaymentProgress(
                        'credits',
                        'processing',
                        'Your LLM Usage Credits balance changed. Confirming whether a recharge is now required.',
                    );
                }
                if (creditCompletion.state === 'succeeded' || creditCompletion.state === 'skipped')
                {
                    updatePaymentProgress(
                        'credits',
                        'succeeded',
                        creditCompletion.state === 'skipped'
                            ? creditCompletion.message || 'Existing LLM Usage Credits remain available.'
                            : 'Initial LLM Usage Credits payment completed.',
                    );
                    initialCreditCompleted = true;
                    break;
                }
                if (creditCompletion.state === 'failed')
                {
                    const message = creditCompletion.message || 'The initial LLM Usage Credits payment failed.';
                    updatePaymentProgress('credits', 'failed', message);
                    setErrorMsg(message);
                    await loadData();
                    return;
                }
                if (creditCompletion.state === 'requires_action')
                {
                    if (!stripe || !creditCompletion.client_secret || !creditCompletion.stripe_payment_intent_id)
                    {
                        throw new Error('Stripe payment verification is not ready for the initial LLM Usage Credits charge.');
                    }
                    updatePaymentProgress('credits', 'verifying', 'Complete the card verification window to finish the credit payment.');
                    const confirmation = await stripe.confirmCardPayment(creditCompletion.client_secret);
                    if (confirmation.error)
                    {
                        throw new Error(confirmation.error.message || 'Initial LLM Usage Credits payment verification was not completed.');
                    }
                    if (confirmation.paymentIntent?.status === 'succeeded')
                    {
                        await confirmLLMUsageCreditsRecharge(creditCompletion.stripe_payment_intent_id);
                        updatePaymentProgress('credits', 'processing', 'Stripe is confirming the initial LLM Usage Credits payment.');
                    }
                }
                await new Promise((resolve) => window.setTimeout(resolve, INITIAL_CREDIT_COMPLETION_POLL_DELAY_MS));
            }
            if (!initialCreditCompleted)
            {
                throw new Error('Your subscription is active, but the initial LLM Usage Credits payment is still processing. Please wait a moment and try again.');
            }

            setSuccessMsg('Subscription and initial LLM Usage Credits payments completed.');
            await loadData();
            window.setTimeout(() => setPaymentProgress(null), 1200);
        }
        catch (err: any)
        {
            const message = err?.response?.data?.detail || err?.message || 'Subscription payment recovery failed.';
            setErrorMsg(message);
            updatePaymentProgress('subscription', 'failed', message);
            updatePaymentProgress('credits', 'failed', 'Not started because the subscription payment was not completed.');
            try
            {
                setPaymentRecovery(await fetchPaymentRecovery());
            }
            catch
            {
                // Keep the current recovery details visible when refresh is unavailable.
            }
        }
        finally
        {
            setRecoveringSubscriptionPayment(false);
        }
    }

    async function submitPaymentSupportTicket(): Promise<void>
    {
        if (!supportPayment) return;
        setSupportSubmitting(true);
        try
        {
            const ticket = await createBillingSupportTicket({ payment_id: supportPayment.id, reason: supportReason, description: supportDescription });
            setSuccessMsg(`Support ticket ${ticket.public_id} was submitted.`);
            setSupportPayment(null);
            setSupportDescription('');
        }
        catch (err: any)
        {
            setErrorMsg(err?.response?.data?.detail || err?.message || 'Unable to submit the support ticket.');
        }
        finally { setSupportSubmitting(false); }
    }

    // ---------------------------------------------------------------------------
    // Computed values
    // ---------------------------------------------------------------------------

    const isActive = subStatus?.has_active_subscription === true && ['active', 'trialing'].includes(subStatus.subscription_status || '');
    const cancelScheduled = subStatus?.cancel_at_period_end === true;
    const cancellationDate = subStatus?.cancel_at || subStatus?.current_period_end;
    const hasSavedPaymentMethod = paymentMethod?.has_payment_method === true;
    const recoveryNeedsPaymentMethod = paymentRecovery !== null && !hasSavedPaymentMethod;
    const recoveryNeedsVerification = paymentRecovery?.status === 'requires_action';
    const jobsBlocked = quota ? !quota.may_run_job : false;
    const stripeCardActionsAvailable = stripeConfigured && !stripeLoadError;
    const stripeStatusMessage = stripeLoadError || stripeConfigError;
    const noPlanMessage = subStatus?.message || 'No subscription plan is available for your organization. Please contact the AcceliQA Team.';
    const noPaidPlansAvailable = availablePlans !== null && availablePlans.paid_plans.length === 0;
    const showPlanUnavailableState = noPaidPlansAvailable || Boolean(subStatus?.message && !subStatus.plan);
    const creditSummary = quota?.llm_usage_credits || subStatus?.llm_usage_credits || null;
    const rechargeBalanceCents = creditSummary?.available_balance_after_debt_cents
        ?? creditSummary?.balance_cents
        ?? 0;
    const rechargeAmountCents = creditSummary?.recharge_amount_cents ?? 0;
    const projectedRechargeBalanceCents = rechargeBalanceCents + rechargeAmountCents;
    const isOnboardingBilling = onboardingBillingRequested && !isActive;
    const betaQuotaExpiresAt = currentPlanInfo?.free_quota_expires_at
        ? new Date(currentPlanInfo.free_quota_expires_at)
        : null;
    const betaQuotaActive = Boolean(
        currentPlanInfo?.is_beta_quota &&
        betaQuotaExpiresAt &&
        !Number.isNaN(betaQuotaExpiresAt.getTime()) &&
        betaQuotaExpiresAt.getTime() > Date.now(),
    );
    const canCollectPaymentMethod = !showPlanUnavailableState;
    const planDescription = currentPlanInfo?.plan?.description;
    const showPlanDescription = Boolean(
        planDescription && !planDescription.startsWith('Default free plan'),
    );

    useEffect(() =>
    {
        if (onboardingBillingRequested && isActive && !onboardingBillingCompleted)
        {
            markOnboardingBillingDecisionCompleted(getGuidedTourStepIndexById('project-name'));
        }
    }, [isActive, onboardingBillingCompleted, onboardingBillingRequested]);

    const planInfo = subStatus?.plan ?? {
        name: 'Subscription Plan',
        description: 'Plan details are currently unavailable.',
        plan_type: 'paid',
        price_cents: 0,
        price_usd: 0,
        currency: 'usd',
        billing_interval: 'month',
        is_active: true,
    };

    function renderOnboardingDecision(): React.ReactNode
    {
        if (!isOnboardingBilling)
        {
            return null;
        }

        if (betaQuotaActive)
        {
            return (
                <div id="billing-onboarding-decision" className="sb-onboarding-card">
                    <div className="sb-onboarding-copy">
                        <div className="sb-onboarding-kicker">First-time setup</div>
                        <h2>Your early-access quota is ready.</h2>
                        <p>
                            Your organization can start creating test cases with its early-access quota. Continue to set up your first project.
                        </p>
                        {quota && (
                            <div className="sb-onboarding-quota">
                                <span>Early-access quota remaining</span>
                                <strong>{formatTokens(quota.free_tokens_remaining)}</strong>
                            </div>
                        )}
                    </div>
                    <div className="sb-onboarding-actions">
                        <button
                            id="billing-continue-project-setup"
                            type="button"
                            className="sb-btn-primary"
                            onClick={completeOnboardingBillingDecision}
                        >
                            Continue to Project Setup
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div id="billing-onboarding-decision" className="sb-onboarding-card">
                <div className="sb-onboarding-copy">
                    <div className="sb-onboarding-kicker">First-time setup</div>
                    <h2>Choose how this organization will run Test Case Creation.</h2>
                    <p>
                        You can continue with the free quota now, or save a card and activate the subscription before creating your first project.
                    </p>
                    {quota && (
                        <div className="sb-onboarding-quota">
                            <span>Free quota remaining</span>
                            <strong>{formatTokens(quota.free_tokens_remaining)}</strong>
                        </div>
                    )}
                </div>
                <div className="sb-onboarding-actions">
                    <button
                        id="billing-continue-free-quota"
                        type="button"
                        className="sb-btn-primary"
                        onClick={handleContinueWithFreeQuota}
                    >
                        Continue with Free Quota
                    </button>
                    <button
                        type="button"
                        className="sb-btn-secondary"
                        onClick={handleFocusSubscriptionPath}
                    >
                        Set Up Subscription
                    </button>
                </div>
            </div>
        );
    }

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    if (loading)
    {
        return (
            <div className="sb-page">
                <div className="sb-loading">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading billing information…
                </div>
            </div>
        );
    }

    return (
        <div className="sb-page">
            <BillingPaymentProgressModal
                show={paymentProgress !== null}
                steps={paymentProgress || []}
                onHide={() => setPaymentProgress(null)}
            />
            <Modal
                show={supportPayment !== null}
                onHide={() => !supportSubmitting && setSupportPayment(null)}
                dialogClassName="sb-report-issue-modal"
                centered
                backdrop={supportSubmitting ? 'static' : true}
                keyboard={!supportSubmitting}
            >
                <Modal.Header closeButton={!supportSubmitting} className="sb-report-issue-header">
                    <div className="sb-report-issue-heading">
                        <span className="sb-report-issue-icon" aria-hidden="true"><ExclamationTriangleFill size={17} /></span>
                        <div>
                            <Modal.Title>Report a charge issue</Modal.Title>
                            <p>Tell us what needs attention and our billing team will review it.</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="sb-report-issue-body">
                    <div className="sb-report-issue-charge">
                        <span>Charge to review</span>
                        <strong>{supportPayment ? formatUsd(supportPayment.amount_cents) : '—'}</strong>
                        <small>{supportPayment?.payment_type?.replace(/_/g, ' ') || 'Billing payment'}</small>
                    </div>
                    <div className="sb-report-issue-field">
                        <span className="sb-form-label">Reason</span>
                        <CustomDropdown
                            value={supportReason}
                            options={SUPPORT_TICKET_REASON_OPTIONS}
                            onChange={setSupportReason}
                            disabled={supportSubmitting}
                        />
                    </div>
                    <div className="sb-report-issue-field">
                        <label className="sb-form-label" htmlFor="billing-support-details">Details</label>
                        <textarea id="billing-support-details" className="sb-filter-input sb-report-issue-details" rows={4} value={supportDescription} onChange={(event) => setSupportDescription(event.target.value)} disabled={supportSubmitting} placeholder="Describe the issue and any details that can help us investigate." />
                        <p className="sb-form-hint">Please include at least 10 characters.</p>
                    </div>
                </Modal.Body>
                <Modal.Footer className="sb-report-issue-footer">
                    <button type="button" className="sb-btn-secondary" onClick={() => setSupportPayment(null)} disabled={supportSubmitting}>Cancel</button>
                    <button type="button" className="sb-btn-primary" onClick={() => void submitPaymentSupportTicket()} disabled={supportSubmitting || supportDescription.trim().length < 10}>{supportSubmitting ? <><Spinner animation="border" size="sm" /> Submitting...</> : 'Submit ticket'}</button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={showRechargeCreditsConfirm}
                onHide={() => !rechargingCredits && setShowRechargeCreditsConfirm(false)}
                dialogClassName="sb-recharge-confirm-modal"
                backdrop={rechargingCredits ? 'static' : true}
                keyboard={!rechargingCredits}
                centered
            >
                <Modal.Header closeButton={!rechargingCredits} className="sb-recharge-confirm-header">
                    <div className="sb-recharge-confirm-heading">
                        <span className="sb-recharge-confirm-icon" aria-hidden="true"><LightningFill size={18} /></span>
                        <div>
                            <Modal.Title>Recharge LLM Usage Credits</Modal.Title>
                            <p>Review the payment before continuing.</p>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body className="sb-recharge-confirm-body">
                    <p className="sb-recharge-confirm-copy">
                        You currently have <strong>{formatUsd(rechargeBalanceCents)}</strong> available in LLM Usage Credits.
                    </p>
                    <div className="sb-recharge-confirm-summary">
                        <div>
                            <span>Credits to add</span>
                            <strong>{formatUsd(rechargeAmountCents)}</strong>
                        </div>
                        <div>
                            <span>Balance after recharge</span>
                            <strong>{formatUsd(projectedRechargeBalanceCents)}</strong>
                        </div>
                    </div>
                    <p className="sb-recharge-confirm-note">
                        Your saved payment method will be charged when you confirm. Stripe may ask you to verify the payment.
                    </p>
                </Modal.Body>
                <Modal.Footer className="sb-recharge-confirm-footer">
                    <button
                        type="button"
                        className="sb-btn-primary"
                        onClick={() => void handleManualCreditsRecharge()}
                        disabled={rechargingCredits || rechargeAmountCents <= 0}
                    >
                        <CreditCard2FrontFill size={14} /> Recharge {formatUsd(rechargeAmountCents)}
                    </button>
                    <button
                        type="button"
                        className="sb-btn-danger"
                        onClick={() => setShowRechargeCreditsConfirm(false)}
                        disabled={rechargingCredits}
                    >
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>
            {/* ---- Page header ---- */}
            <div className="sb-header">
                <div>
                    <h1 className="sb-title">Subscription &amp; Billing</h1>
                    <p className="sb-subtitle">
                        Manage your plan, LLM Usage Credits, and payment history.
                    </p>
                </div>
                <button
                    className="sb-btn-secondary"
                    onClick={handleBillingRefresh}
                    disabled={loading}
                    title="Refresh"
                    type="button"
                >
                    <ArrowClockwise size={14} /> Refresh
                </button>
            </div>

            {renderOnboardingDecision()}

            {/* ---- Global alerts ---- */}
            {pageError && (
                <div className="sb-alert sb-alert-danger">
                    <XCircleFill size={16} className="flex-shrink-0" />
                    <span>{pageError}</span>
                </div>
            )}
            {successMsg && (
                <div className="sb-alert sb-alert-success">
                    <CheckCircleFill size={16} className="flex-shrink-0" />
                    <span>{successMsg}</span>
                </div>
            )}
            {errorMsg && (
                <div className="sb-alert sb-alert-danger">
                    <XCircleFill size={16} className="flex-shrink-0" />
                    <span>{errorMsg}</span>
                </div>
            )}
            {paymentRecovery?.is_draft && (
                <div className="sb-alert sb-alert-warning sb-recovery-alert">
                    <ExclamationTriangleFill size={16} className="flex-shrink-0" />
                    <span className="sb-recovery-copy">
                        <strong>Renewal payment is being prepared.</strong>{' '}
                        {formatTimeUntil(paymentRecovery.automatically_finalizes_at)
                            ? `Stripe is scheduled to prepare it in ${formatTimeUntil(paymentRecovery.automatically_finalizes_at)} (by ${formatDateTime(paymentRecovery.automatically_finalizes_at)}).`
                            : 'Stripe will prepare it shortly.'}{' '}
                        Your current subscription remains active, so you can continue running jobs while the renewal payment is prepared.
                    </span>
                </div>
            )}
            {paymentRecovery && !paymentRecovery.is_draft && (
                <div className="sb-alert sb-alert-warning sb-recovery-alert">
                    <ExclamationTriangleFill size={16} className="flex-shrink-0" />
                    <span className="sb-recovery-copy">
                        <strong>
                            {recoveryNeedsPaymentMethod
                                ? 'Subscription payment needs a payment method.'
                                : recoveryNeedsVerification
                                ? 'Payment verification required.'
                                : 'Subscription payment needs attention.'}
                        </strong>{' '}
                        {recoveryNeedsPaymentMethod
                            ? 'Save a payment method, then retry the outstanding invoice when you are ready.'
                            : paymentRecovery.failure_message
                            || (paymentRecovery.next_payment_attempt
                                ? `Stripe will retry on ${formatDate(paymentRecovery.next_payment_attempt)}.`
                                : 'Retry the latest invoice using your saved payment method.')}
                    </span>
                    <button
                        type="button"
                        className="sb-btn-primary sb-recovery-action"
                        onClick={() =>
                        {
                            if (recoveryNeedsPaymentMethod)
                            {
                                handleAddPaymentMethodForRecovery();
                                return;
                            }
                            void handleRecoverSubscriptionPayment();
                        }}
                        disabled={
                            removingPaymentMethod
                            || (!recoveryNeedsPaymentMethod && (recoveringSubscriptionPayment || !stripeCardActionsAvailable))
                        }
                    >
                        {recoveryNeedsPaymentMethod
                            ? <><CreditCard2FrontFill size={14} /> Add payment method</>
                            : recoveringSubscriptionPayment
                            ? <><Spinner animation="border" size="sm" /> Recovering…</>
                            : recoveryNeedsVerification ? 'Verify payment' : 'Retry payment'}
                    </button>
                </div>
            )}
            {creditSummary?.settlement_pending && (
                <div className="sb-alert sb-alert-warning sb-recovery-alert">
                    <ExclamationTriangleFill size={16} className="flex-shrink-0" />
                    <span className="sb-recovery-copy">
                        <strong>LLM Usage Credits settlement is in progress.</strong>{' '}
                        {creditSummary.settlement_amount_cents
                            ? `${Math.max(1, Math.ceil((creditSummary.pending_overage_debt_cents || 0) / Math.max(1, creditSummary.recharge_amount_cents || 1)))} ${Math.max(1, Math.ceil((creditSummary.pending_overage_debt_cents || 0) / Math.max(1, creditSummary.recharge_amount_cents || 1))) === 1 ? 'configured recharge is' : 'configured recharges are'} needed to settle the remaining ${formatUsd(creditSummary.pending_overage_debt_cents || 0)} debt. The current ${formatUsd(creditSummary.settlement_amount_cents)} installment is being processed.`
                            : 'Your in-flight usage is being settled before new paid AI work can start.'}{' '}
                        Complete any required payment verification to resume queued work.
                    </span>
                </div>
            )}
            {showPlanUnavailableState && (
                <div className="sb-alert sb-alert-warning">
                    <ExclamationTriangleFill size={16} className="flex-shrink-0" />
                    <span>{noPlanMessage}</span>
                </div>
            )}
            {quota && !quota.has_active_subscription && quota.active_balance_source === 'free' && (
                <div className="sb-alert sb-alert-success">
                    <CheckCircleFill size={16} className="flex-shrink-0" />
                    <span>
                        <strong>Free quota active.</strong>{' '}
                        Your organization has {formatTokens(quota.free_tokens_remaining)} free tokens remaining.
                        {quota.free_overage_debt > 0 && quota.free_overage_debt_cents > 0 && (
                            <> Over-consumed tokens ({formatTokens(quota.free_overage_debt)} / {formatUsdAmount(quota.free_overage_debt_usd)}) will be carried as a pending billing adjustment when you subscribe.</>
                        )}
                    </span>
                </div>
            )}
            {quota && !showPlanUnavailableState && !quota.has_active_subscription && quota.active_balance_source !== 'free' && (
                <div className="sb-alert sb-alert-warning">
                    <ExclamationTriangleFill size={16} className="flex-shrink-0" />
                    <span>
                        {quota.free_tokens_allocated > 0 ? (
                            <>
                                <strong>Free quota exhausted.</strong>{' '}
                                Add a payment method and subscribe to the <strong>{planInfo.name}</strong> to continue running jobs.
                                {quota.free_overage_debt > 0 && quota.free_overage_debt_cents > 0 && (
                                    <> {formatTokens(quota.free_overage_debt)} over-consumed tokens ({formatUsdAmount(quota.free_overage_debt_usd)}) will be carried as a pending billing adjustment.</>
                                )}
                            </>
                        ) : (
                            <>
                                <strong>No active subscription.</strong>{' '}
                                Subscribe to the <strong>{planInfo.name}</strong> to run AI jobs.
                                Without an active subscription, new jobs will be blocked.
                            </>
                        )}
                    </span>
                </div>
            )}
            {quota?.has_active_subscription && jobsBlocked && (
                <div className="sb-alert sb-alert-danger">
                    <ExclamationTriangleFill size={16} className="flex-shrink-0" />
                    <span>
                        <strong>Job access paused.</strong>{' '}
                        {quota.block_reason || 'Resolve the billing issue to continue running jobs.'}
                    </span>
                </div>
            )}
            {quota && quota.free_overage_debt > 0 && quota.free_overage_debt_cents > 0 && (
                <div className="sb-alert sb-alert-warning">
                    <ExclamationTriangleFill size={16} className="flex-shrink-0" />
                    <span>
                        <strong>Free-plan overage recorded.</strong>{' '}
                        {formatTokens(quota.free_overage_debt)} tokens are over-consumed, currently valued at {formatUsdAmount(quota.free_overage_debt_usd)}.
                    </span>
                </div>
            )}

            {/* ---- Current Plan panel ---- */}
            {currentPlanInfo && currentPlanInfo.plan && (
                <div className="sb-current-plan-card">
                    <div className="sb-current-plan-header">
                        <div className="sb-current-plan-titleblock">
                            <span className="sb-current-plan-eyebrow">Current Plan</span>
                            <h2 className="sb-current-plan-name">
                                {currentPlanInfo.plan.name}
                                <span className={`sb-current-plan-pill ${currentPlanInfo.is_free_plan ? 'sb-current-plan-pill-free' : 'sb-current-plan-pill-paid'}`}>
                                    {currentPlanInfo.plan.plan_type}
                                </span>
                            </h2>
                            {showPlanDescription && (
                                <p className="sb-current-plan-desc">{planDescription}</p>
                            )}
                        </div>
                    </div>
                    <div className="sb-current-plan-grid">
                        <div className="sb-current-plan-stat">
                            <span className="sb-current-plan-stat-label">Price</span>
                            <span className="sb-current-plan-stat-value">
                                {currentPlanInfo.is_free_plan
                                    ? 'Free'
                                    : `${formatUsd(currentPlanInfo.plan.price_cents)} / ${formatBillingInterval(currentPlanInfo.plan.billing_interval)}`}
                            </span>
                        </div>
                        <div className="sb-current-plan-stat">
                            <span className="sb-current-plan-stat-label">Usage model</span>
                            <span className="sb-current-plan-stat-value">
                                {currentPlanInfo.is_free_plan
                                    ? `${formatTokens(currentPlanInfo.effective_free_token_quota ?? currentPlanInfo.plan.free_token_quota ?? 0)} free tokens`
                                    : 'LLM Usage Credits'}
                            </span>
                        </div>
                        {currentPlanInfo.is_free_plan && currentPlanInfo.free_quota_expires_at && (
                            <div className="sb-current-plan-stat">
                                <span className="sb-current-plan-stat-label">Trial ends</span>
                                <span className="sb-current-plan-stat-value">
                                    {formatDate(currentPlanInfo.free_quota_expires_at)}
                                </span>
                                {currentPlanInfo.free_quota_period_days != null && (
                                    <span className="sb-current-plan-stat-sub">
                                        {currentPlanInfo.free_quota_period_days}-day trial
                                    </span>
                                )}
                            </div>
                        )}
                        {!currentPlanInfo.is_free_plan && subStatus?.has_active_subscription && (
                            <>
                                <div className="sb-current-plan-stat">
                                    <span className="sb-current-plan-stat-label">Period start</span>
                                    <span className="sb-current-plan-stat-value">{formatDate(subStatus.current_period_start)}</span>
                                </div>
                                <div className="sb-current-plan-stat">
                                    <span className="sb-current-plan-stat-label">Period end</span>
                                    <span className="sb-current-plan-stat-value">{formatDate(subStatus.current_period_end)}</span>
                                </div>
                                {cancelScheduled && (
                                    <div className="sb-current-plan-stat">
                                        <span className="sb-current-plan-stat-label">Cancels on</span>
                                        <span className="sb-current-plan-stat-value">{formatDate(cancellationDate)}</span>
                                    </div>
                                )}
                                {subStatus?.pending_plan && (
                                    <div className="sb-current-plan-stat">
                                        <span className="sb-current-plan-stat-label">Next plan</span>
                                        <span className="sb-current-plan-stat-value">{subStatus.pending_plan.name}</span>
                                        <span className="sb-current-plan-stat-sub">
                                            Starts {formatDate(subStatus.pending_plan_effective_at || subStatus.current_period_end)}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                        <div className="sb-current-plan-stat">
                            <span className="sb-current-plan-stat-label">User limit</span>
                            <span className="sb-current-plan-stat-value">
                                {currentPlanInfo.effective_user_limit == null
                                    ? 'Unlimited'
                                    : `${currentPlanInfo.effective_user_limit} ${currentPlanInfo.effective_user_limit === 1 ? 'user' : 'users'}`}
                            </span>
                        </div>
                    </div>

                    {/* Subscription actions: upgrade / cancel / resubscribe */}
                    <div className="sb-current-plan-actions">
                        <hr className="sb-section-divider" />
                        {showCancelConfirm ? (
                                <div>
                                    <p className="sb-confirm-text">
                                        Are you sure you want to cancel? Access continues until{' '}
                                        <strong>{formatDate(subStatus?.current_period_end)}</strong>.
                                    </p>
                                    <div className="sb-confirm-actions">
                                        <button
                                            type="button"
                                            className="sb-btn-secondary"
                                            onClick={() => setShowCancelConfirm(false)}
                                            disabled={cancelling}
                                        >
                                            Keep Subscription
                                        </button>
                                        <button
                                            type="button"
                                            className="sb-btn-danger"
                                            onClick={() => void handleCancel()}
                                            disabled={cancelling}
                                        >
                                            {cancelling ? (
                                                <><Spinner animation="border" size="sm" /> Cancelling…</>
                                            ) : (
                                                <><XLg size={13} /> Yes, Cancel</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="sb-current-plan-btn-row">
                                    <button
                                        type="button"
                                        className="sb-btn-primary"
                                        onClick={() => navigate('/billing/upgrade')}
                                    >
                                        <LightningFill size={14} /> {currentPlanInfo.is_free_plan ? 'Upgrade Plan' : 'Change Plan'}
                                    </button>
                                    {isActive && !cancelScheduled && !currentPlanInfo.is_free_plan && (
                                        <button
                                            type="button"
                                            className="sb-btn-danger"
                                            onClick={() => setShowCancelConfirm(true)}
                                        >
                                            Cancel Subscription
                                        </button>
                                    )}
                                    {isActive && cancelScheduled && !currentPlanInfo.is_free_plan && (
                                        hasSavedPaymentMethod ? (
                                            <button
                                                type="button"
                                                className="sb-btn-primary"
                                                onClick={() => void handleResumeSubscription()}
                                                disabled={resumingSubscription}
                                            >
                                                {resumingSubscription ? (
                                                    <><Spinner animation="border" size="sm" /> Restoring…</>
                                                ) : (
                                                    <><ArrowClockwise size={14} /> Resume subscription</>
                                                )}
                                            </button>
                                        ) : (
                                            <span className="sb-form-hint">Add a payment method to restore auto-renewal.</span>
                                        )
                                    )}
                                </div>
                            )}
                    </div>
                </div>
            )}

            {/* ---- Content cards ---- */}
            <div className="sb-right-col">
                    {/* Subscription status card */}
                    <div className="sb-card">
                        <h2 className="sb-card-title">Subscription Status</h2>
                        <p className="sb-card-sub">Current billing period overview</p>

                        <div className="sb-status-row">
                            <span className={`sb-status-badge ${statusClass(subStatus?.subscription_status ?? null)}`}>
                                {subStatus?.has_active_subscription
                                    ? <><CheckCircleFill size={10} /> {subStatus.subscription_status}</>
                                    : 'No Subscription'
                                }
                            </span>
                        </div>

                        {!subStatus?.has_active_subscription && (
                            <div className="sb-subscribe-cta">
                                {availablePlans && availablePlans.paid_plans.length > 0 ? (
                                    <button
                                        type="button"
                                        className="sb-btn-primary"
                                        onClick={() => navigate('/billing/upgrade')}
                                    >
                                        <LightningFill size={15} /> Subscribe Now
                                    </button>
                                ) : (
                                    <p className="sb-form-hint">{noPlanMessage}</p>
                                )}
                            </div>
                        )}

                        {subStatus?.has_active_subscription && (
                            <div className="sb-stat-strip">
                                <div className="sb-stat-item">
                                    <span className="sb-stat-label">Period Start</span>
                                    <span className="sb-stat-value">
                                        {formatDate(subStatus.current_period_start)}
                                    </span>
                                </div>
                                <div className="sb-stat-item">
                                    <span className="sb-stat-label">{cancelScheduled ? 'Cancellation Date' : 'Renewal Date'}</span>
                                    <span className="sb-stat-value">
                                        {formatDate(cancelScheduled ? cancellationDate : subStatus.current_period_end)}
                                    </span>
                                </div>
                                <div className="sb-stat-item">
                                    <span className="sb-stat-label">Plan Price</span>
                                    <span className="sb-stat-value">
                                        {formatUsd(planInfo.price_cents)} / {formatBillingInterval(planInfo.billing_interval)}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div
                        id="billing-payment-method-card"
                        className="sb-card"
                        data-has-payment-method={hasSavedPaymentMethod ? 'true' : 'false'}
                    >
                        <h2 className="sb-card-title">Payment Method</h2>
                        <p className="sb-card-sub">
                            {showPlanUnavailableState
                                ? 'Payment setup will be available after a subscription plan is published for your organization.'
                                : 'Use the controls below to save, edit, or delete the card used for subscription renewals.'}
                        </p>

                        {stripeStatusMessage && (
                            <div className="sb-alert sb-alert-warning">
                                <ExclamationTriangleFill size={16} className="flex-shrink-0" />
                                <span>
                                    {stripeStatusMessage}{' '}
                                    You can still view or delete a saved payment method, but saving or editing a card is currently unavailable.
                                </span>
                            </div>
                        )}

                        {hasSavedPaymentMethod && !showPaymentMethodForm && (
                            <div className="sb-payment-method-summary">
                                    <div className="sb-payment-method-main">
                                        <div className="sb-payment-method-title">
                                            {formatCardBrand(paymentMethod?.brand)} ending in {paymentMethod?.last4 || '••••'}
                                        </div>
                                        <div className="sb-payment-method-meta">
                                            Expires {formatCardExpiry(paymentMethod?.exp_month, paymentMethod?.exp_year)}
                                            {paymentMethod?.cardholder_name ? ` • ${paymentMethod.cardholder_name}` : ''}
                                        </div>
                                        {paymentMethod?.customer_id && (
                                            <div className="sb-payment-method-meta">
                                                Customer ID: {paymentMethod.customer_id}
                                            </div>
                                        )}
                                    </div>

                                    <div className="sb-inline-actions">
                                        <button
                                            type="button"
                                            className="sb-btn-primary"
                                            onClick={() =>
                                            {
                                                setShowRemovePaymentConfirm(false);
                                                setShowPaymentMethodForm(true);
                                                setCardComplete(false);
                                                setErrorMsg(null);
                                            }}
                                            disabled={!canCollectPaymentMethod || !stripeCardActionsAvailable || !stripe || removingPaymentMethod}
                                        >
                                            <PencilSquare size={14} /> Edit
                                        </button>
                                        <button
                                            type="button"
                                            className="sb-btn-danger"
                                            onClick={() => setShowRemovePaymentConfirm((current) => !current)}
                                            disabled={removingPaymentMethod}
                                        >
                                            {showRemovePaymentConfirm ? <XLg size={14} /> : <Trash3Fill size={14} />} {showRemovePaymentConfirm ? 'Cancel' : 'Delete'}
                                        </button>
                                    </div>

                                    {showRemovePaymentConfirm && (
                                        <div className="sb-confirm-block">
                                            <p className="sb-confirm-text">
                                                Delete the saved card ending in {paymentMethod?.last4 || '••••'}?
                                                {subStatus?.has_active_subscription
                                                    ? ' This will also cancel your subscription at the end of the current billing period.'
                                                    : ' Future renewals may require a new payment method.'}
                                            </p>
                                            <div className="sb-confirm-actions">
                                                <button
                                                    type="button"
                                                    className="sb-btn-secondary"
                                                    onClick={() => setShowRemovePaymentConfirm(false)}
                                                    disabled={removingPaymentMethod}
                                                >
                                                    <XLg size={14} /> Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    className="sb-btn-danger"
                                                    onClick={() => void handleRemovePaymentMethod()}
                                                    disabled={removingPaymentMethod}
                                                >
                                                    {removingPaymentMethod ? (
                                                        <><Spinner animation="border" size="sm" /> Deleting…</>
                                                    ) : (
                                                        <><Trash3Fill size={14} /> Delete</>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                        )}

                        {!hasSavedPaymentMethod && (
                            <div className="sb-empty-state">
                                <p className="sb-quota-empty-msg">
                                    {showPlanUnavailableState
                                        ? noPlanMessage
                                        : 'No payment method is saved yet. Enter the new card details below to add one now.'}
                                </p>
                            </div>
                        )}

                        {canCollectPaymentMethod && (showPaymentMethodForm || !hasSavedPaymentMethod) && stripeCardActionsAvailable && (
                            <form onSubmit={(e) => void handleSavePaymentMethod(e)}>
                                <div id="billing-card-details-field">
                                    <label className="sb-form-label">
                                        {hasSavedPaymentMethod ? 'New card details' : 'Card details'}
                                    </label>
                                    <div id="billing-card-details" className="sb-stripe-wrapper">
                                        <CardElement
                                            options={CARD_OPTIONS}
                                            onChange={(e) => setCardComplete(e.complete)}
                                        />
                                    </div>
                                    <p className="sb-form-hint">
                                        Secured by Stripe. {hasSavedPaymentMethod
                                            ? 'Saving a new card replaces the current default payment method.'
                                            : 'The card will be stored as the default payment method for billing.'}
                                    </p>
                                </div>
                                <div className="sb-subscribe-actions">
                                    {hasSavedPaymentMethod && (
                                        <button
                                            type="button"
                                            className="sb-btn-secondary sb-btn-flex-1"
                                            onClick={() =>
                                            {
                                                closeCardForms();
                                                setErrorMsg(null);
                                            }}
                                            disabled={savingPaymentMethod}
                                        >
                                            <XLg size={14} /> Cancel
                                        </button>
                                    )}
                                    <button
                                        id="billing-save-payment-method"
                                        type="submit"
                                        className={`sb-btn-primary ${hasSavedPaymentMethod ? 'sb-btn-flex-2' : 'sb-btn-flex-1'}`}
                                        disabled={!cardComplete || savingPaymentMethod || !stripe}
                                    >
                                        {savingPaymentMethod ? (
                                            <><Spinner animation="border" size="sm" /> Saving…</>
                                        ) : (
                                            <><CreditCard2FrontFill size={15} /> Save</>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Usage and billing card */}
                    <div className="sb-card">
                        <h2 className="sb-card-title">Usage &amp; Billing Period</h2>
                        <p className="sb-card-sub">
                            {quota?.active_balance_source === 'free'
                                ? 'Your organization is using the free token quota.'
                                : quota?.active_balance_source === 'byo'
                                    ? 'This organization is in BYO mode. Regular LLM jobs use your configured provider key, while vector-ingestion and embedding calls are deducted from prepaid LLM Usage Credits.'
                                : 'Platform-mode LLM usage is deducted from prepaid LLM Usage Credits.'}
                        </p>

                        {quota && (quota.has_active_subscription || quota.free_tokens_allocated > 0) ? (
                            <>
                                {/* Free quota section */}
                                {quota.free_tokens_allocated > 0 && (
                                    <>
                                        <div className="sb-quota-row">
                                            <span>Free Quota</span>
                                            <span className="sb-quota-numbers">
                                                {formatTokens(quota.free_tokens_used)} / {formatTokens(quota.free_tokens_allocated)}
                                            </span>
                                        </div>
                                        <progress
                                            className={`sb-progress-track sb-progress-track-${
                                                quota.free_tokens_allocated > 0 && quota.free_tokens_remaining <= 0
                                                    ? 'danger'
                                                    : quota.free_tokens_used / quota.free_tokens_allocated > 0.7
                                                        ? 'warning'
                                                        : 'safe'
                                            }`}
                                            value={quota.free_tokens_allocated > 0
                                                ? Math.min(100, (quota.free_tokens_used / quota.free_tokens_allocated) * 100)
                                                : 0}
                                            max={100}
                                        />
                                        <div className="sb-stat-strip">
                                            <div className="sb-stat-item">
                                                <span className="sb-stat-label">Free Used</span>
                                                <span className="sb-stat-value">
                                                    {quota.free_tokens_used.toLocaleString()}
                                                </span>
                                            </div>
                                            <div className="sb-stat-item">
                                                <span className="sb-stat-label">Free Remaining</span>
                                                <span className="sb-stat-value sb-value-success">
                                                    {quota.free_tokens_remaining.toLocaleString()}
                                                </span>
                                            </div>
                                            {quota.free_overage_debt > 0 && quota.free_overage_debt_cents > 0 && (
                                                <div className="sb-stat-item">
                                                    <span className="sb-stat-label">Overage Debt</span>
                                                    <span className="sb-stat-value sb-value-danger">
                                                        {quota.free_overage_debt.toLocaleString()}
                                                    </span>
                                                </div>
                                            )}
                                            {quota.free_overage_debt_cents > 0 && (
                                                <div className="sb-stat-item">
                                                    <span className="sb-stat-label">Overage Debt USD</span>
                                                    <span className="sb-stat-value sb-value-danger">
                                                        {formatUsdAmount(quota.free_overage_debt_usd)}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* LLM Usage Credits section */}
                                {quota.has_active_subscription && (
                                    <>
                                        {quota.free_tokens_allocated > 0 && (
                                            <hr className="sb-section-divider" />
                                        )}
                                        <div className="sb-quota-row">
                                            <span>LLM Usage Credits</span>
                                            <span className="sb-quota-numbers">
                                                {formatUsd(creditSummary?.available_balance_after_debt_cents ?? creditSummary?.balance_cents ?? 0)} available
                                            </span>
                                        </div>

                                        <div className="sb-stat-strip">
                                            <div className="sb-stat-item">
                                                <span className="sb-stat-label">Pending Debt</span>
                                                <span className="sb-stat-value">
                                                    {formatUsd(creditSummary?.pending_overage_debt_cents ?? 0)}
                                                </span>
                                            </div>
                                            <div className="sb-stat-item">
                                                <span className="sb-stat-label">Available LLM Usage Credits</span>
                                                <span className="sb-stat-value">
                                                    {formatUsd(creditSummary?.available_balance_after_debt_cents ?? creditSummary?.balance_cents ?? 0)}
                                                </span>
                                            </div>
                                            <div className="sb-stat-item">
                                                <span className="sb-stat-label">Auto-Recharge At</span>
                                                <span className="sb-stat-value">
                                                    {formatUsd(creditSummary?.auto_recharge_threshold_cents ?? 0)}
                                                </span>
                                            </div>
                                            <div className="sb-stat-item">
                                                <span className="sb-stat-label">Recharge Amount</span>
                                                <span className="sb-stat-value">
                                                    {formatUsd(creditSummary?.recharge_amount_cents ?? 0)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="sb-stat-strip sb-quota-footer-spaced">
                                            <div className="sb-stat-item">
                                                <span className="sb-stat-label">Paid Platform Tokens</span>
                                                <span className="sb-stat-value">
                                                    {quota.all_time_paid_platform_tokens.toLocaleString()}
                                                </span>
                                            </div>
                                            <div className="sb-stat-item">
                                                <span className="sb-stat-label">BYO Tokens</span>
                                                <span className="sb-stat-value">
                                                    {quota.byo_recorded_tokens.toLocaleString()}
                                                </span>
                                            </div>
                                            <div className="sb-stat-item">
                                                <span className="sb-stat-label">BYO Charge</span>
                                                <span className="sb-stat-value">
                                                    {formatUsdAmount(quota.byo_recorded_charge_usd)}
                                                </span>
                                            </div>
                                        </div>

                                        {creditSummary?.failure_message && (
                                            <div className="sb-alert sb-alert-danger sb-quota-footer-spaced">
                                                <ExclamationTriangleFill size={16} className="flex-shrink-0" />
                                                <span>{creditSummary.failure_message}</span>
                                            </div>
                                        )}

                                        <div className="sb-inline-actions sb-quota-footer-spaced">
                                            <button
                                                type="button"
                                                className="sb-btn-secondary"
                                                onClick={() => setShowRechargeCreditsConfirm(true)}
                                                disabled={rechargingCredits || !hasSavedPaymentMethod}
                                            >
                                                {rechargingCredits ? (
                                                    <><Spinner animation="border" size="sm" /> Recharging…</>
                                                ) : (
                                                    <><CreditCard2FrontFill size={14} /> Recharge Credits</>
                                                )}
                                            </button>
                                        </div>

                                        {subStatus?.pending_adjustments && subStatus.pending_adjustments.length > 0 && (
                                            <div className="sb-stat-strip sb-quota-footer-spaced">
                                                {subStatus.pending_adjustments.map((adjustment) => (
                                                    <div className="sb-stat-item" key={adjustment.id}>
                                                        <span className="sb-stat-label">Pending Adjustment</span>
                                                        <span className="sb-stat-value">
                                                            {formatUsdAmount(adjustment.amount_usd)} {adjustment.source_type.replace(/_/g, ' ')}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <p className="sb-quota-footer sb-quota-footer-spaced">
                                            Billing period renews on{' '}
                                            <strong>{formatDate(quota.period_end)}</strong>.
                                            {jobsBlocked && (
                                                <span className="sb-blocked-note">
                                                    Jobs are currently blocked until billing access is restored.
                                                </span>
                                            )}
                                            {quota.outstanding_total_overage_debt > 0 && quota.free_overage_debt_cents > 0 && (
                                                <span className="sb-blocked-note">
                                                    {formatTokens(quota.outstanding_total_overage_debt)} over-consumed free tokens are pending billing reconciliation.
                                                </span>
                                            )}
                                        </p>
                                    </>
                                )}
                            </>
                        ) : (
                            <p className="sb-quota-empty-msg">
                                Subscribe to the {planInfo.name} to start running AI jobs with LLM Usage Credits.
                            </p>
                        )}
                    </div>

                    {/* Token usage history card */}
                    <div className="sb-card">
                        <h2 className="sb-card-title">Token Usage History</h2>
                        <p className="sb-card-sub">Recent token-consuming jobs and quota adjustments in your organization.</p>
                        {usageError && <div className="sb-inline-error">{usageError}</div>}

                        {/* Ledger events (grants, deductions, rollovers) */}
                        <>
                            <h3 className="sb-section-title">Quota Adjustments &amp; Ledger Events</h3>
                            {renderLedgerFilters()}
                            <div className="sb-table-wrap sb-table-scroll sb-ledger-table-wrap">
                                <table className="sb-table" data-responsive-cards="true">
                                    <thead>
                                        <tr>
                                            <th>Date &amp; Time</th>
                                            <th>Event</th>
                                            <th>Bucket</th>
                                            <th>Tokens</th>
                                            <th>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {usageLoading && usageLoadingSection === 'ledger' ? (
                                            <tr className="sb-empty-row">
                                                <td colSpan={5}><Spinner animation="border" size="sm" className="me-2" />Searching ledger events...</td>
                                            </tr>
                                        ) : displayLedgerEntries.length === 0 ? (
                                            <tr className="sb-empty-row">
                                                <td colSpan={5}>No quota ledger events found</td>
                                            </tr>
                                        ) : displayLedgerEntries.map((entry) => (
                                            <tr key={`ledger-${entry.id}`}>
                                                <td data-label="Date & Time">{formatDateTime(entry.created_at)}</td>
                                                <td data-label="Event">{formatEventType(entry.event_type)}</td>
                                                <td className="sb-td-muted" data-label="Bucket">{entry.balance_bucket}</td>
                                                <td className={`sb-td-amount ${entry.token_delta >= 0 ? 'sb-value-success' : 'sb-value-danger'}`} data-label="Tokens">
                                                    {entry.token_delta >= 0 ? '+' : ''}{entry.token_delta.toLocaleString()}
                                                </td>
                                                <td className="sb-td-muted" data-label="Notes">{entry.notes || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>

                        {/* Aggregated usage entries */}
                        <h3 className="sb-section-title">Usage by Job / Request</h3>
                        {renderUsageFilters()}
                        <div className="sb-table-wrap sb-table-scroll">
                            <table className="sb-table" data-responsive-cards="true">
                                <thead>
                                    <tr>
                                        <th>Date &amp; Time</th>
                                        <th>Reference</th>
                                        <th>Feature</th>
                                        <th>Model</th>
                                        <th>Calls</th>
                                        <th>Key</th>
                                        <th>Billable</th>
                                        <th>LLM Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usageLoading && usageLoadingSection === 'usage' ? (
                                        <tr className="sb-empty-row">
                                            <td colSpan={8}><Spinner animation="border" size="sm" className="me-2" />Searching usage history...</td>
                                        </tr>
                                    ) : tokenUsageHistory.length === 0 ? (
                                        <tr className="sb-empty-row">
                                            <td colSpan={8}>No token usage history yet</td>
                                        </tr>
                                    ) : (
                                        tokenUsageHistory.map((entry) => (
                                            <tr key={entry.usage_group_key || entry.id}>
                                                <td data-label="Date & Time">{formatDateTime(entry.created_at)}</td>
                                                <td className="sb-td-muted" data-label="Reference">{formatUsageReference(entry)}</td>
                                                <td className="sb-td-muted" data-label="Feature">{entry.usage_label || formatWorkflowProfile(entry.workflow_profile)}</td>
                                                <td className="sb-td-muted" data-label="Model">{entry.model || '—'}</td>
                                                <td data-label="Calls">{entry.event_count.toLocaleString()}</td>
                                                <td className="sb-td-muted" data-label="Key">{formatKeySource(entry.key_source)}</td>
                                                <td className={entry.billable_tokens > 0 ? 'sb-value-success' : 'sb-td-muted'} data-label="Billable">
                                                    {formatUsageBillingStatus(entry)}
                                                </td>
                                                <td className="sb-td-amount" data-label="LLM Cost">{formatLlmUsdAmount(entry.customer_charge_usd)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Payment history card */}
                    {false && <div className="sb-card">
                        <h2 className="sb-card-title">Payment History</h2>
                        <p className="sb-card-sub">All billing transactions for your organization.</p>
                        {renderPaymentFilters()}
                        {paymentsError && <div className="sb-inline-error">{paymentsError}</div>}

                        <div className="sb-table-wrap sb-table-scroll sb-payment-history-wrap">
                            <table className="sb-table" data-responsive-cards="true">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Period</th>
                                        <th>Receipt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentsLoading ? (
                                        <tr className="sb-empty-row">
                                            <td colSpan={6}><Spinner animation="border" size="sm" className="me-2" />Searching payments...</td>
                                        </tr>
                                    ) : payments.length === 0 ? (
                                        <tr className="sb-empty-row">
                                            <td colSpan={6}>No payments yet</td>
                                        </tr>
                                    ) : (
                                        payments.map((p) => (
                                            <tr key={p.id}>
                                                <td data-label="Date">{formatDate(p.paid_at || p.created_at)}</td>
                                                <td data-label="Type">{formatPaymentType(p)}</td>
                                                <td className="sb-td-amount" data-label="Amount">
                                                    <div>{formatUsd(p.amount_cents)}</div>
                                                    {p.status === 'partially_refunded' && (p.refunded_amount_cents || 0) > 0 && (
                                                        <div className="sb-refund-breakdown">
                                                            Refunded {formatUsd(p.refunded_amount_cents || 0)} | Retained {formatUsd(Math.max(0, p.amount_cents - (p.refunded_amount_cents || 0)))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td data-label="Status">
                                                    <span className={`sb-pay-status ${payStatusClass(p.status)}`}>
                                                        {formatPaymentStatus(p.status)}
                                                    </span>
                                                </td>
                                                <td className="sb-td-muted" data-label="Period">
                                                    {p.period_start
                                                        ? `${formatDate(p.period_start)} – ${formatDate(p.period_end)}`
                                                        : '—'}
                                                </td>
                                                <td data-label="Receipt" data-card-actions="true">
                                                    <div className="sb-payment-actions">
                                                        {p.receipt_url ? (
                                                            <button
                                                                type="button"
                                                                className="sb-payment-link"
                                                                onClick={() => window.open(p.receipt_url!, '_blank', 'noopener,noreferrer')}
                                                                title={p.stripe_payment_intent_id || p.stripe_invoice_id || 'Stripe receipt'}
                                                            >
                                                                View receipt
                                                            </button>
                                                        ) : (
                                                            <span className="sb-td-muted">—</span>
                                                        )}
                                                        {['succeeded', 'partially_refunded'].includes(p.status) && Math.max(0, p.amount_cents - (p.refunded_amount_cents || 0)) > 0 && (
                                                            <button type="button" className="sb-report-issue-button" onClick={() => setSupportPayment(p)}>Report issue</button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>}

                </div>
        </div>
    );
}
